import axios from 'axios';
import { getToken, clearToken } from './tokenStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════════════════════════════
// CRM TENANT LANGUAGE — multi-tenant hard segmentation (IT/ES/EN)
// ★ 2026-08-28 (Phase 57.CRM-LANG.4)
// ═══════════════════════════════════════════════════════════════════════════
// L'admin sceglie con il selettore in AppShell quale CRM tenant vede.
// Il valore è persistito in localStorage e propagato via CustomEvent così tutti
// gli hook che dipendono da esso possono re-fetch quando cambia.
const CRM_TENANT_STORAGE_KEY = 'crm_tenant_language';
export const CRM_TENANT_CHANGE_EVENT = 'gp:crm-tenant-change';
// ★ 2026-08-28 (Phase 57.CRM-LANG.6) — 'global' aggrega tutto (admin only)
export const CRM_TENANT_LANGUAGES = ['global', 'it', 'es', 'en'];

/** Ritorna il tenant CRM corrente (default: `it`). */
export function getCrmTenant() {
  try {
    const v = localStorage.getItem(CRM_TENANT_STORAGE_KEY);
    return CRM_TENANT_LANGUAGES.includes(v) ? v : 'it';
  } catch {
    return 'it';
  }
}

/** Imposta il tenant CRM e notifica gli listener. */
export function setCrmTenant(lang) {
  const norm = String(lang || '').toLowerCase();
  if (!CRM_TENANT_LANGUAGES.includes(norm)) return;
  try {
    localStorage.setItem(CRM_TENANT_STORAGE_KEY, norm);
    window.dispatchEvent(new CustomEvent(CRM_TENANT_CHANGE_EVENT, { detail: { language: norm } }));
  } catch { /* noop */ }
}

/**
 * Endpoint pattern che devono essere filtrati per tenant language.
 * Aggiungiamo il param `language` solo se il chiamante non l'ha già settato
 * (così i funnel pubblici non vengono influenzati).
 * ★ 2026-08-28 (Phase 57.CRM-LANG.7) — Esteso a agents/tickets/commissions/payments
 */
const LANGUAGE_AWARE_PATTERNS = [
  /^\/leads(\/|$|\?)/i,
  /^\/leads\/trash\/count/i,
  /^\/properties(\/|$|\?)/i,
  /^\/proposal-orders(\/|$|\?)/i,
  /^\/proposals-library(\/|$|\?)/i,
  /^\/agents(\/|$|\?)/i,
  /^\/tickets(\/|$|\?)/i,
  /^\/admin\/agent-commissions(\/|$|\?)/i,
  /^\/agent-commissions\/summary/i,
  /^\/agent-commissions\/monthly-dashboard/i,
  /^\/payments(\/|$|\?)/i,
  // ★ 2026-08-28 (Phase 57.CRM-LANG.8) — KPI/Dashboard/Pipeline/Classifica/Reports
  /^\/kpis(\/|$|\?)/i,
  /^\/kpis-extended(\/|$|\?)/i,
  /^\/leaderboard(\/|$|\?)/i,
  /^\/pipeline(\/|$|\?)/i,
  /^\/reports(\/|$|\?)/i,
  /^\/dashboard(\/|$|\?)/i,
  /^\/bookings(\/|$|\?)/i,
  // ★ Fase 21 — Calendario separato per tenant CRM (eventi filtrati per lingua lead)
  /^\/calendar(\/|$|\?)/i,
  // ★ Fase 24 — Conversazioni WhatsApp separate per tenant CRM
  /^\/whatsapp\/conversations(\/|$|\?)/i,
];

function isLanguageAwareUrl(url = '') {
  return LANGUAGE_AWARE_PATTERNS.some((rx) => rx.test(url));
}

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  // ★ 2026-07-29 — centralized token access via tokenStorage.
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // ★ 2026-08-28 (Phase 57.CRM-LANG.7) — Header con tenant CRM attivo.
  // Serve al backend per assegnare automaticamente `language` ai nuovi record
  // creati via POST/PATCH (leads, tickets, commissions, ...).
  try {
    config.headers = config.headers || {};
    config.headers['X-Crm-Tenant'] = getCrmTenant();
  } catch { /* noop */ }
  // ★ 2026-08-28 — inject tenant language for CRM endpoints (GET only).
  const method = String(config.method || 'get').toLowerCase();
  const url = config.url || '';
  if (method === 'get' && isLanguageAwareUrl(url)) {
    config.params = config.params || {};
    if (config.params.language === undefined) {
      const tenant = getCrmTenant();
      config.params.language = tenant;
    }
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      try { clearToken(); } catch { /* no-op */ }
    }
    return Promise.reject(err);
  }
);
